import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { ArrowRight, Lock } from "lucide-react";

/**
 * Privacy Policy — public route (no auth required) so Google Cloud
 * Console's OAuth Consent Screen can crawl it for verification.
 * URL must match the value entered in the consent screen exactly: /privacy.
 *
 * The component renders one of two language-specific subtrees based on
 * the active i18n language. Hebrew is preserved verbatim from the
 * pre-i18n baseline — every word, list item, and emphasis block is
 * unchanged.
 *
 * Last updated: May 18, 2026 / 1 Sivan 5786
 */
export default function Privacy() {
  const { i18n } = useTranslation();
  if (i18n.language === "en") return <PrivacyEn />;
  return <PrivacyHe />;
}

function PrivacyHe() {
  return (
    <div
      className="mx-auto max-w-3xl px-4 sm:px-6 py-12 md:py-20"
      data-testid="privacy-page"
      dir="rtl"
    >
      <header className="mb-10 text-center">
        <div className="flex justify-center mb-4">
          <div className="h-12 w-12 rounded-full bg-gold/10 border border-gold/40 flex items-center justify-center">
            <Lock className="h-5 w-5 text-gold" strokeWidth={1.5} />
          </div>
        </div>
        <h1 className="font-heading text-4xl md:text-5xl">מדיניות פרטיות</h1>
        <p className="font-latin italic text-sm text-gold/80 mt-2">
          תוריא · Torahya
        </p>
        <p className="text-sm text-muted-foreground mt-1">
          תאריך עדכון אחרון: 18 במאי 2026 · א' סיון תשפ"ו
        </p>
        <div className="ornamental-divider mt-4">✦</div>
      </header>

      <div className="space-y-7 text-foreground leading-relaxed text-[15px]">
        <Section title="מי אנחנו">
          <p>
            תוריא היא אפליקציה ללימוד תורה, מנוהלת על ידי <strong>צוות תוריא</strong>.
            תוריא בתהליך רישום כארגון ללא מטרות רווח (Non-Profit,
            501(c)(3) Pending) בארצות הברית.
          </p>
          <p className="mt-2">
            ליצירת קשר:{" "}
            <a
              href="mailto:7torahya@gmail.com"
              className="text-gold underline"
              data-testid="privacy-contact-email"
            >
              7torahya@gmail.com
            </a>
          </p>
        </Section>

        <Section title="מידע שאנחנו אוספים">
          <p className="font-heading text-foreground">בעת הרשמה:</p>
          <ul className="list-disc pr-6 mt-1 space-y-1">
            <li>שם מלא</li>
            <li>כתובת אימייל</li>
            <li>סיסמא (מוצפנת ב־bcrypt)</li>
            <li>מידע מ-Google OAuth (אם בחרת להתחבר ככה: שם, אימייל, תמונת פרופיל)</li>
          </ul>

          <p className="font-heading text-foreground mt-4">בעת השימוש באפליקציה:</p>
          <ul className="list-disc pr-6 mt-1 space-y-1">
            <li>רשומות שכתבת במחברת</li>
            <li>סימניות והדגשות שבחרת</li>
            <li>שיעורי תורה שבנית</li>
            <li>היסטוריית קריאה</li>
            <li>אירועים אישיים בלוח השנה (יארצייטים, יומי הולדת, שיעורים, תזכורות)</li>
            <li>העדפת מיקום (לזמני שבת)</li>
            <li>מיקומים אחרונים שחיפשת</li>
          </ul>

          <p className="font-heading text-foreground mt-4">בעת תרומה:</p>
          <ul className="list-disc pr-6 mt-1 space-y-1">
            <li>סכום התרומה</li>
            <li>מטרת התרומה (תוריא / ישיבות / צדקה)</li>
            <li>פרטי הקדשה (לעילוי נשמה / להצלחת / לרפואת)</li>
            <li>כתובת אימייל לקבלה</li>
          </ul>

          <Notice>
            פרטי כרטיס האשראי <strong>לא נשמרים אצלנו</strong> — הם מועברים
            ישירות ל-Stripe (חברת התשלומים המובילה בעולם, PCI-DSS Level 1).
          </Notice>

          <p className="font-heading text-foreground mt-4">נתונים אוטומטיים:</p>
          <ul className="list-disc pr-6 mt-1 space-y-1">
            <li>כתובת IP</li>
            <li>סוג הדפדפן ומכשיר</li>
            <li>זמני שימוש (last_seen_at)</li>
            <li>מקור הגעה לאתר (אם הגעת דרך קישור הפניה)</li>
            <li>ערוץ הפצה (WhatsApp, Telegram, Email, וכו')</li>
          </ul>
        </Section>

        <Section title="איך אנחנו משתמשים במידע">
          <ul className="list-disc pr-6 space-y-1">
            <li>לספק את שירותי האפליקציה</li>
            <li>לשלוח קבלות תרומה ותעודות הקדשה</li>
            <li>לשלוח התראות על אבני דרך (לדוגמה: "הבאת 5 חברים")</li>
            <li>לשפר את האפליקציה ולפתח פיצ'רים חדשים</li>
            <li>ליצור איתך קשר במקרה של בעיות</li>
            <li>לעמוד בדרישות חוקיות (בארה"ב ובישראל)</li>
          </ul>
        </Section>

        <Section title="שירותי צד שלישי שאנחנו משתמשים בהם">
          <div className="overflow-x-auto rounded-sm border border-border/50 bg-card/40">
            <table className="w-full text-sm" data-testid="privacy-third-party-table">
              <thead className="text-right text-xs uppercase tracking-wider text-gold/90 bg-gold/5">
                <tr>
                  <th className="px-3 py-2 font-heading">שירות</th>
                  <th className="px-3 py-2 font-heading">למה משתמשים</th>
                  <th className="px-3 py-2 font-heading">מה מועבר</th>
                </tr>
              </thead>
              <tbody className="text-foreground/85">
                <Row s="Google OAuth"          why="התחברות נוחה"          what="אימייל, שם, תמונה" />
                <Row s="Stripe"                why="עיבוד תרומות"          what="סכום, מטבע, פרטי תשלום" />
                <Row s="Resend"                why="שליחת מיילים"          what="אימייל, שם, תוכן" />
                <Row s="HebCal"                why="זמני הלכה ולוח שנה"    what="מיקום (geonameid או lat/lon)" />
                <Row s="Sefaria"               why="טקסטים תורניים"        what="אין מידע אישי" />
                <Row s="OpenStreetMap (Nominatim)" why="זיהוי מיקום מ-GPS" what="קואורדינטות" />
              </tbody>
            </table>
          </div>
        </Section>

        <Section title="מי רואה את המידע שלך">
          <ul className="list-disc pr-6 space-y-1">
            <li><strong>אתה</strong> — גישה מלאה לכל המידע שלך</li>
            <li><strong>צוות תוריא</strong> — גישה טכנית לתמיכה וניהול</li>
            <li><strong>שירותי צד שלישי</strong> — כל אחד רק לפי הצורך</li>
          </ul>

          <Notice>
            <ul className="space-y-1">
              <li>אנחנו <strong>לא מוכרים</strong> את המידע שלך לאיש.</li>
              <li>אנחנו <strong>לא משתפים</strong> עם מפרסמים.</li>
              <li>אנחנו <strong>לא מציגים פרסומות</strong>.</li>
            </ul>
          </Notice>
        </Section>

        <Section title="הזכויות שלך">
          <p>יש לך זכות:</p>
          <ul className="list-disc pr-6 mt-2 space-y-1">
            <li><strong>לראות</strong> איזה מידע יש לנו עליך</li>
            <li><strong>לבקש שינוי</strong> של מידע לא נכון</li>
            <li><strong>למחוק</strong> את החשבון שלך לחלוטין</li>
            <li><strong>להוריד</strong> את המידע שלך (Data Portability)</li>
            <li><strong>לבטל</strong> הסכמה לאיסוף מידע (יחייב סגירת חשבון)</li>
          </ul>
          <p className="mt-3 text-sm">
            לכל בקשה:{" "}
            <a href="mailto:7torahya@gmail.com" className="text-gold underline">
              7torahya@gmail.com
            </a>
            {" "}— נטפל תוך 30 ימים.
          </p>
        </Section>

        <Section title="עוגיות (Cookies & Local Storage)">
          <p>אנחנו משתמשים ב:</p>
          <ul className="list-disc pr-6 mt-2 space-y-1">
            <li><strong>JWT tokens</strong> — להישאר מחובר</li>
            <li><strong>localStorage</strong> — לשמירת העדפות (מיקום נוכחי, סל שיעורים, מיקומים אחרונים)</li>
            <li><strong>Session cookies</strong> — לאבטחה</li>
          </ul>
          <Notice>
            אנחנו <strong>לא</strong> משתמשים ב-Cookies לפרסום או לעיקוב צד שלישי.
          </Notice>
        </Section>

        <Section title="אבטחה">
          <ul className="list-disc pr-6 space-y-1">
            <li>סיסמאות מוצפנות (bcrypt)</li>
            <li>חיבור HTTPS בכל ההעברות</li>
            <li>תרומות מאובטחות ע"י Stripe (PCI-DSS Level 1)</li>
            <li>שרתי MongoDB מוגנים</li>
            <li>גיבויים יומיים אוטומטיים</li>
          </ul>
          <Notice>
            למרות מאמצינו — אף מערכת אינה בטוחה לחלוטין. ההמלצה: השתמש בסיסמא
            חזקה ייחודית.
          </Notice>
        </Section>

        <Section title="ילדים מתחת לגיל 13">
          תוריא אינה מיועדת לילדים מתחת לגיל 13. אם הינך הורה ונודע לך שילדך
          נרשם — אנא צור איתנו קשר ונמחק את החשבון.
        </Section>

        <Section title="תושבי האיחוד האירופי (GDPR)">
          <p>אם אתה תושב EU — יש לך זכויות נוספות תחת GDPR:</p>
          <ul className="list-disc pr-6 mt-2 space-y-1">
            <li>זכות לסיוע משפטי</li>
            <li>זכות להתנגד לעיבוד</li>
            <li>זכות לשמירת מידע מינימלית</li>
          </ul>
          <p className="mt-3 text-sm">
            ליצירת קשר:{" "}
            <a href="mailto:7torahya@gmail.com" className="text-gold underline">
              7torahya@gmail.com
            </a>
          </p>
        </Section>

        <Section title="תושבי קליפורניה (CCPA)">
          <p>אם אתה תושב קליפורניה — יש לך זכויות תחת CCPA:</p>
          <ul className="list-disc pr-6 mt-2 space-y-1">
            <li>לדעת איזה מידע נאסף עליך</li>
            <li>למחוק את המידע</li>
            <li>שלא ימכרו את המידע שלך (תוריא לעולם לא מוכרת מידע)</li>
          </ul>
        </Section>

        <Section title="שינויים במדיניות">
          נעדכן את הדף הזה אם נשנה את המדיניות. שינויים משמעותיים — נודיע
          במייל לכל המשתמשים הרשומים.
        </Section>

        <Section title="יצירת קשר">
          <p>
            לכל שאלה:{" "}
            <a
              href="mailto:7torahya@gmail.com"
              className="text-gold underline"
            >
              7torahya@gmail.com
            </a>
          </p>
        </Section>
      </div>

      <div className="mt-12 pt-6 border-t border-border/40 text-center">
        <Link
          to="/"
          data-testid="privacy-back-home"
          className="inline-flex items-center gap-2 text-sm font-heading text-gold hover:text-gold/80 transition-colors"
        >
          <ArrowRight className="h-4 w-4 rtl:rotate-180" strokeWidth={1.5} />
          חזרה לדף הבית
        </Link>
      </div>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <section>
      <h2 className="font-heading text-xl md:text-2xl text-[#1E3A5F] dark:text-gold mb-2">
        {title}
      </h2>
      <div className="text-foreground/85 space-y-2">{children}</div>
    </section>
  );
}

/**
 * Gold-bordered emphasis block for "⚠️"-style notices the user needs
 * to actually read.
 */
function Notice({ children }) {
  return (
    <div className="my-3 rounded-sm border-r-4 border-gold/70 bg-gold/5 px-4 py-3 text-sm text-foreground/90">
      {children}
    </div>
  );
}

function Row({ s, why, what }) {
  return (
    <tr className="border-t border-border/40 last:border-b-0">
      <td className="px-3 py-2 font-heading whitespace-nowrap">{s}</td>
      <td className="px-3 py-2">{why}</td>
      <td className="px-3 py-2 text-muted-foreground">{what}</td>
    </tr>
  );
}

// ---------------------------------------------------------------- //
// English version — independent LTR subtree. Mirrors the Hebrew copy
// section-for-section so an English-mode user reads the same scope of
// privacy commitments without seeing untranslated Hebrew.
// ---------------------------------------------------------------- //
function PrivacyEn() {
  return (
    <div
      className="mx-auto max-w-3xl px-4 sm:px-6 py-12 md:py-20"
      data-testid="privacy-page"
      dir="ltr"
    >
      <header className="mb-10 text-center">
        <div className="flex justify-center mb-4">
          <div className="h-12 w-12 rounded-full bg-gold/10 border border-gold/40 flex items-center justify-center">
            <Lock className="h-5 w-5 text-gold" strokeWidth={1.5} />
          </div>
        </div>
        <h1 className="font-heading text-4xl md:text-5xl">Privacy Policy</h1>
        <p className="font-latin italic text-sm text-gold/80 mt-2">
          Torahya · תוריא
        </p>
        <p className="text-sm text-muted-foreground mt-1">
          Last updated: May 18, 2026 · 1 Sivan 5786
        </p>
        <div className="ornamental-divider mt-4">✦</div>
      </header>

      <div className="space-y-7 text-foreground leading-relaxed text-[15px]">
        <SectionEn title="Who we are">
          <p>
            Torahya is a Torah-learning application operated by the <strong>Torahya team</strong>.
            Torahya is currently in the process of registering as a non-profit organization
            (Non-Profit, 501(c)(3) Pending) in the United States.
          </p>
          <p className="mt-2">
            Contact:{" "}
            <a
              href="mailto:7torahya@gmail.com"
              className="text-gold underline"
              data-testid="privacy-contact-email"
            >
              7torahya@gmail.com
            </a>
          </p>
        </SectionEn>

        <SectionEn title="Information we collect">
          <p className="font-heading text-foreground">When you sign up:</p>
          <ul className="list-disc pl-6 mt-1 space-y-1">
            <li>Full name</li>
            <li>Email address</li>
            <li>Password (hashed with bcrypt)</li>
            <li>Google OAuth profile data (if you choose Google sign-in: name, email, profile picture)</li>
          </ul>

          <p className="font-heading text-foreground mt-4">When you use the app:</p>
          <ul className="list-disc pl-6 mt-1 space-y-1">
            <li>Entries you save in your notebook</li>
            <li>Bookmarks and highlights you create</li>
            <li>Torah lessons you build</li>
            <li>Reading history</li>
            <li>Personal calendar events (yahrzeits, birthdays, lessons, reminders)</li>
            <li>Location preference (for halachic times)</li>
            <li>Recent locations you searched for</li>
          </ul>

          <p className="font-heading text-foreground mt-4">When you donate:</p>
          <ul className="list-disc pl-6 mt-1 space-y-1">
            <li>Donation amount</li>
            <li>Cause (Torahya / Yeshivot / Tzedakah)</li>
            <li>Dedication details (in memory of / for the success of / for the recovery of)</li>
            <li>Email address for the receipt</li>
          </ul>

          <NoticeEn>
            Your credit card details are <strong>never stored on our servers</strong> — they
            are passed directly to Stripe (the leading payment processor, PCI-DSS Level 1).
          </NoticeEn>

          <p className="font-heading text-foreground mt-4">Automatically collected:</p>
          <ul className="list-disc pl-6 mt-1 space-y-1">
            <li>IP address</li>
            <li>Browser and device type</li>
            <li>Usage timestamps (last_seen_at)</li>
            <li>Referral source (if you arrived through an invite link)</li>
            <li>Distribution channel (WhatsApp, Telegram, Email, etc.)</li>
          </ul>
        </SectionEn>

        <SectionEn title="How we use your information">
          <ul className="list-disc pl-6 space-y-1">
            <li>To provide the application's services</li>
            <li>To send donation receipts and dedication certificates</li>
            <li>To send milestone notifications (e.g. "You brought in 5 friends")</li>
            <li>To improve the app and develop new features</li>
            <li>To contact you if a problem arises</li>
            <li>To comply with legal requirements (US and Israel)</li>
          </ul>
        </SectionEn>

        <SectionEn title="Third-party services we use">
          <div className="overflow-x-auto rounded-sm border border-border/50 bg-card/40">
            <table className="w-full text-sm" data-testid="privacy-third-party-table">
              <thead className="text-left text-xs uppercase tracking-wider text-gold/90 bg-gold/5">
                <tr>
                  <th className="px-3 py-2 font-heading">Service</th>
                  <th className="px-3 py-2 font-heading">Purpose</th>
                  <th className="px-3 py-2 font-heading">What we send</th>
                </tr>
              </thead>
              <tbody className="text-foreground/85">
                <Row s="Google OAuth"              why="Easy sign-in"          what="Email, name, picture" />
                <Row s="Stripe"                    why="Donation processing"   what="Amount, currency, payment details" />
                <Row s="Resend"                    why="Sending email"         what="Email, name, content" />
                <Row s="HebCal"                    why="Halachic times & calendar" what="Location (geonameid or lat/lon)" />
                <Row s="Sefaria"                   why="Torah texts"           what="No personal data" />
                <Row s="OpenStreetMap (Nominatim)" why="Location lookup from GPS" what="Coordinates" />
              </tbody>
            </table>
          </div>
        </SectionEn>

        <SectionEn title="Who sees your data">
          <ul className="list-disc pl-6 space-y-1">
            <li><strong>You</strong> — full access to all your data</li>
            <li><strong>The Torahya team</strong> — technical access for support and operations</li>
            <li><strong>Third-party services</strong> — each only on a need-to-know basis</li>
          </ul>

          <NoticeEn>
            <ul className="space-y-1">
              <li>We <strong>never sell</strong> your data to anyone.</li>
              <li>We <strong>do not share</strong> with advertisers.</li>
              <li>We <strong>do not show advertisements</strong>.</li>
            </ul>
          </NoticeEn>
        </SectionEn>

        <SectionEn title="Your rights">
          <p>You have the right:</p>
          <ul className="list-disc pl-6 mt-2 space-y-1">
            <li>To <strong>view</strong> what information we hold about you</li>
            <li>To <strong>request correction</strong> of inaccurate data</li>
            <li>To <strong>delete</strong> your account completely</li>
            <li>To <strong>export</strong> your data (Data Portability)</li>
            <li>To <strong>withdraw consent</strong> for data collection (will require account closure)</li>
          </ul>
          <p className="mt-3 text-sm">
            For any request:{" "}
            <a href="mailto:7torahya@gmail.com" className="text-gold underline">
              7torahya@gmail.com
            </a>
            {" "}— we will respond within 30 days.
          </p>
        </SectionEn>

        <SectionEn title="Cookies & Local Storage">
          <p>We use:</p>
          <ul className="list-disc pl-6 mt-2 space-y-1">
            <li><strong>JWT tokens</strong> — to keep you signed in</li>
            <li><strong>localStorage</strong> — to remember preferences (current location, lesson basket, recent locations)</li>
            <li><strong>Session cookies</strong> — for security</li>
          </ul>
          <NoticeEn>
            We <strong>do not</strong> use cookies for advertising or third-party tracking.
          </NoticeEn>
        </SectionEn>

        <SectionEn title="Security">
          <ul className="list-disc pl-6 space-y-1">
            <li>Passwords are hashed (bcrypt)</li>
            <li>All traffic is over HTTPS</li>
            <li>Donations are secured by Stripe (PCI-DSS Level 1)</li>
            <li>MongoDB servers are protected</li>
            <li>Automatic daily backups</li>
          </ul>
          <NoticeEn>
            Despite our efforts — no system is completely secure. Recommendation:
            use a strong, unique password.
          </NoticeEn>
        </SectionEn>

        <SectionEn title="Children under 13">
          Torahya is not intended for children under the age of 13. If you are
          a parent and discover that your child has registered — please contact
          us and we will delete the account.
        </SectionEn>

        <SectionEn title="Not a substitute for rabbinic or professional advice">
          The Torah content presented in Torahya — including AI-generated
          explanations, chavruta replies, and aggadic insights — is for
          learning and study. It is <strong>not a halachic ruling</strong> and
          is <strong>not a substitute</strong> for consulting a competent
          rabbi for practical halacha, nor for medical, mental, or
          professional care.
        </SectionEn>

        <SectionEn title="EU residents (GDPR)">
          <p>If you are an EU resident — you have additional rights under GDPR:</p>
          <ul className="list-disc pl-6 mt-2 space-y-1">
            <li>The right to data minimisation</li>
            <li>The right to object to processing</li>
            <li>The right to lodge a complaint with a supervisory authority</li>
          </ul>
          <p className="mt-3 text-sm">
            Contact:{" "}
            <a href="mailto:7torahya@gmail.com" className="text-gold underline">
              7torahya@gmail.com
            </a>
          </p>
        </SectionEn>

        <SectionEn title="California residents (CCPA)">
          <p>If you are a California resident — you have rights under CCPA:</p>
          <ul className="list-disc pl-6 mt-2 space-y-1">
            <li>To know what data is collected about you</li>
            <li>To request deletion of your data</li>
            <li>Not to have your data sold (Torahya never sells data)</li>
          </ul>
        </SectionEn>

        <SectionEn title="Changes to this policy">
          We will update this page if we change the policy. For material
          changes — we will notify all registered users by email.
        </SectionEn>

        <SectionEn title="Contact">
          <p>
            For any question:{" "}
            <a
              href="mailto:7torahya@gmail.com"
              className="text-gold underline"
            >
              7torahya@gmail.com
            </a>
          </p>
        </SectionEn>
      </div>

      <div className="mt-12 pt-6 border-t border-border/40 text-center">
        <Link
          to="/"
          data-testid="privacy-back-home"
          className="inline-flex items-center gap-2 text-sm font-heading text-gold hover:text-gold/80 transition-colors"
        >
          <ArrowRight className="h-4 w-4 rotate-180" strokeWidth={1.5} />
          Back to home
        </Link>
      </div>
    </div>
  );
}

function SectionEn({ title, children }) {
  return (
    <section>
      <h2 className="font-heading text-xl md:text-2xl text-[#1E3A5F] dark:text-gold mb-2">
        {title}
      </h2>
      <div className="text-foreground/85 space-y-2">{children}</div>
    </section>
  );
}

function NoticeEn({ children }) {
  return (
    <div className="my-3 rounded-sm border-l-4 border-gold/70 bg-gold/5 px-4 py-3 text-sm text-foreground/90">
      {children}
    </div>
  );
}
